import { useState, useEffect } from 'react'
import {
	useQuery,
	useQueryClient,
	keepPreviousData,
} from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl, apiClient } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import type { DateRange } from 'react-day-picker'
import ResponsesTable, {
	ResponsesTableSkeleton,
} from '@/components/responses/ResponsesTable'
import { ResponseDetailView } from '@/components/responses/ResponseDetailView'
import { Search, RotateCcw, AlertTriangle, List, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { Response as WidgetResponse } from '@/lib/widget-schema'
import { useProgram } from '@/hooks/useProgram'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { Database } from '@/integrations/supabase/types'
import { useTeamContext } from '@/context/TeamContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useTranslation } from '@/hooks/useTranslation'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationPrevious,
	PaginationNext,
	PaginationLink,
	PaginationEllipsis,
} from '@/components/ui/pagination'

type TeamMember = Database['public']['Tables']['team_members']['Row']
type Team = Database['public']['Tables']['teams']['Row']

interface ApiResponseType {
	responses: WidgetResponse[]
	totalMatchingResponses: number
	totalLifetimeResponses: number
	viewableLimit: number
	hiddenCount: number
	hardSubmissionLimit: number
	isPro: boolean
	hasMore: boolean
	currentPage: number
	itemsPerPage: number
	totalPages: number
}


const Responses = () => {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const [dateRange, setDateRange] = useState<DateRange>({
		from: new Date('2025-01-01'),
		to: new Date(),
	})
	const [submissionTypeFilter, setSubmissionTypeFilter] = useState<
		'all' | 'nps' | 'csat' | 'custom' | 'review' | 'testimonial'
	>('all')
	const [scoreFilter, setScoreFilter] = useState('all')
	const [sort, setSort] = useState('created_at_desc')
	const { teamId } = useTeamContext()
	const { programId, loading: programLoading } = useProgram()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const [currentPage, setCurrentPage] = useState(
		parseInt(searchParams.get('page') || '1', 10)
	)
	const itemsPerPage = 10
	const [selectedResponseId, setSelectedResponseId] = useState<string | null>(
		null
	)


	const {
		data: apiResponse,
		isLoading,
		refetch,
		error,
	} = useQuery<ApiResponseType, Error>({
		queryKey: [
			'responses',
			teamId,
			programId,
			dateRange,
			submissionTypeFilter,
			currentPage,
			itemsPerPage,
		],
		queryFn: async () => {
			if (!teamId) throw new Error(t('responses.noTeamIdFound'))
			if (!dateRange.from) throw new Error(t('responses.startDateRequired'))

			const startDate = dateRange.from.toISOString()
			const endDate = dateRange.to
				? new Date(dateRange.to.setHours(23, 59, 59, 999)).toISOString()
				: new Date(dateRange.from.setHours(23, 59, 59, 999)).toISOString()

			// Build query parameters
			const params = new URLSearchParams()
			params.append('team_id', teamId)
			params.append('start_date', startDate)
			params.append('end_date', endDate)
			if (programId) {
				params.append('program_id', programId)
			}
			if (submissionTypeFilter !== 'all') {
				params.append('submission_type', submissionTypeFilter)
			}
			params.append('limit', String(itemsPerPage))
			params.append(
				'offset',
				String((currentPage - 1) * itemsPerPage)
			)

			// Use apiClient instead of fetch to include authentication headers
			const data = await apiClient<ApiResponseType>(
				`/api/responses/limited?${params.toString()}`,
				{ method: 'GET', needsAuth: true }
			)
			return data
		},
		enabled: !!teamId && !!dateRange.from && !programLoading,
		placeholderData: keepPreviousData,
	})

	// Effect to reset pagination when filters change
	useEffect(() => {
		// Only reset if we're not already on page 1
		if (currentPage !== 1) {
			setCurrentPage(1)
			setSearchParams({ page: '1' }, { replace: true })
		}
	}, [dateRange, submissionTypeFilter, programId, search])

	// Effect to sync currentPage with URL params (only on mount and URL changes)
	useEffect(() => {
		const pageFromUrl = parseInt(searchParams.get('page') || '1', 10)
		// Only update if URL page is different and valid
		if (pageFromUrl !== currentPage && pageFromUrl > 0) {
			setCurrentPage(pageFromUrl)
		}
	}, [searchParams.get('page')])

	// Effect to sync selectedResponseId with URL params
	useEffect(() => {
		const responseIdFromUrl = searchParams.get('responseId')
		if (responseIdFromUrl && responseIdFromUrl !== selectedResponseId) {
			setSelectedResponseId(responseIdFromUrl)
		} else if (!responseIdFromUrl && selectedResponseId) {
			setSelectedResponseId(null)
		}
	}, [searchParams, selectedResponseId])

	const handlePageChange = (newPage: number) => {
		setCurrentPage(newPage)
		setSearchParams({ page: String(newPage) }, { replace: true })
	}

	const resetFilters = () => {
		setSearch('')
		setDateRange({ from: new Date('2025-01-01'), to: new Date() })
		setSubmissionTypeFilter('all')
		setScoreFilter('all')
		handlePageChange(1)
	}

	const handleSortChange = (newSort: string) => {
		setSort(newSort)
		setCurrentPage(1)
		setSearchParams({ page: '1' }, { replace: true })
	}

	if (error) {
		return (
			<div className="p-4 md:p-8 max-w-7xl mx-auto">
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-8 text-center">
						<AlertTriangle className="h-8 w-8 text-yellow-500 mb-4" />
						<h3 className="text-lg font-semibold mb-2">
							{t('responses.unableToLoadResponses')}
						</h3>
						<p className="text-muted-foreground mb-4">
							{error.message || t('responses.errorLoadingResponses')}
						</p>
						<Button onClick={() => refetch()}>{t('responses.retry')}</Button>
					</CardContent>
				</Card>
			</div>
		)
	}

	// Apply sorting to API responses first, then filter
	const sortedApiResponses = [...(apiResponse?.responses || [])].sort(
		(a, b) => {
			const [sortKey, sortDirection] = sort.split('_')
			const isDesc = sortDirection === 'desc'

			switch (sortKey) {
				case 'created_at':
					const dateA = new Date(a.created_at).getTime()
					const dateB = new Date(b.created_at).getTime()
					return isDesc ? dateB - dateA : dateA - dateB

				default:
					return 0
			}
		}
	)

	const filteredResponses =
		sortedApiResponses?.filter((response) => {
			const matchesWidgetName =
				response.widgets?.name?.toLowerCase().includes(search.toLowerCase()) ??
				true

			const matchesResponseData = (() => {
				if (!search) return true
				const responseData = response.response_data
				if (Array.isArray(responseData)) {
					return responseData.some((data: any) =>
						data.answer?.toLowerCase().includes(search.toLowerCase())
					)
				}
				if (
					responseData &&
					typeof responseData === 'object' &&
					'customAnswers' in responseData &&
					typeof responseData.customAnswers === 'object'
				) {
					return Object.values(
						responseData.customAnswers as Record<string, any>
					).some((data) =>
						data.answer?.toLowerCase().includes(search.toLowerCase())
					)
				}
				if (
					responseData &&
					typeof responseData === 'object' &&
					'testimonialText' in responseData &&
					typeof responseData.testimonialText === 'string'
				) {
					return responseData.testimonialText
						.toLowerCase()
						.includes(search.toLowerCase())
				}
				return false
			})()

			const responseDate = new Date(response.created_at)
			const matchesDate =
				(!dateRange.from || responseDate >= dateRange.from) &&
				(!dateRange.to || responseDate <= dateRange.to)

			const matchesScore = (() => {
				if (scoreFilter === 'all') return true
				if (response.score === null || response.score === undefined)
					return false

				const submissionType =
					(response as any).submission_type || response.widgets?.type

				if (submissionType === 'nps') {
					return (
						(scoreFilter === 'promoters' && response.score >= 9) ||
						(scoreFilter === 'passives' &&
							response.score >= 7 &&
							response.score <= 8) ||
						(scoreFilter === 'detractors' && response.score <= 6)
					)
				}

				if (submissionType === 'csat') {
					return scoreFilter === `csat_${response.score}`
				}

				if (submissionType === 'review' || submissionType === 'testimonial') {
					if (scoreFilter.startsWith('rating_')) {
						return response.score === parseInt(scoreFilter.split('_')[1], 10)
					}
				}

				return true
			})()

			return (
				(matchesWidgetName || matchesResponseData) &&
				matchesDate &&
				matchesScore
			)
		}) ?? []

	// Les réponses sont déjà triées, pas besoin de tri supplémentaire
	const sortedResponses = filteredResponses

	const totalPages = apiResponse?.totalPages || 1

	return (
		<div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<List className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('responses.responses')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('responses.viewAndManageResponses')}
						</p>
					</div>
				</div>
			</motion.div>

			<Card className="mb-8 p-4">
				<div className="w-full flex flex-col md:flex-row gap-4 items-center">
					<DatePickerWithRange
						className="flex-1"
						date={dateRange}
						setDate={(newRange) =>
							setDateRange(newRange || { from: new Date(), to: new Date() })
						}
					/>

					<Select
						value={submissionTypeFilter}
						onValueChange={(value) =>
							setSubmissionTypeFilter(
								value as
									| 'all'
									| 'nps'
									| 'csat'
									| 'custom'
									| 'review'
									| 'testimonial'
							)
						}>
						<SelectTrigger className="w-full md:w-64">
							<SelectValue placeholder={t('responses.submissionType')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('responses.allSubmissionTypes')}</SelectItem>
							<SelectItem value="nps">{t('responses.npsSurvey')}</SelectItem>
							<SelectItem value="csat">{t('responses.csatSurvey')}</SelectItem>
							<SelectItem value="custom">{t('responses.customSurvey')}</SelectItem>
							<SelectItem value="review">{t('responses.review')}</SelectItem>
							<SelectItem value="testimonial">{t('responses.testimonial')}</SelectItem>
						</SelectContent>
					</Select>

					<Select
						value={scoreFilter}
						onValueChange={(value) => setScoreFilter(value)}>
						<SelectTrigger className="w-full md:w-64">
							<SelectValue placeholder={t('responses.scoreFilter')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('responses.allScoresRatings')}</SelectItem>
							{(submissionTypeFilter === 'nps' ||
								submissionTypeFilter === 'all') && (
								<>
									<SelectItem value="promoters">
										{t('responses.npsPromoters')}
									</SelectItem>
									<SelectItem value="passives">{t('responses.npsPassives')}</SelectItem>
									<SelectItem value="detractors">
										{t('responses.npsDetractors')}
									</SelectItem>
								</>
							)}
							{(submissionTypeFilter === 'csat' ||
								submissionTypeFilter === 'all') && (
								<>
									<SelectItem value="csat_5">
										{t('responses.csatVerySatisfied')}
									</SelectItem>
									<SelectItem value="csat_4">{t('responses.csatSatisfied')}</SelectItem>
									<SelectItem value="csat_3">{t('responses.csatNeutral')}</SelectItem>
									<SelectItem value="csat_2">{t('responses.csatUnsatisfied')}</SelectItem>
									<SelectItem value="csat_1">
										{t('responses.csatVeryUnsatisfied')}
									</SelectItem>
								</>
							)}
							{(submissionTypeFilter === 'review' ||
								submissionTypeFilter === 'testimonial' ||
								submissionTypeFilter === 'all') && (
								<>
									<SelectItem value="rating_5">{t('responses.rating5Stars')}</SelectItem>
									<SelectItem value="rating_4">{t('responses.rating4Stars')}</SelectItem>
									<SelectItem value="rating_3">{t('responses.rating3Stars')}</SelectItem>
									<SelectItem value="rating_2">{t('responses.rating2Stars')}</SelectItem>
									<SelectItem value="rating_1">{t('responses.rating1Star')}</SelectItem>
								</>
							)}
						</SelectContent>
					</Select>

					<div className="relative flex-1">
						<Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder={t('responses.searchByWidgetOrResponse')}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>

					<Button
						variant="outline"
						size="icon"
						className="h-10 w-10 rounded-md flex-shrink-0"
						onClick={resetFilters}
						title={t('responses.resetFilters')}>
						<RotateCcw className="h-4 w-4" />
						<span className="sr-only">{t('responses.resetFilters')}</span>
					</Button>
				</div>
			</Card>

			{apiResponse && !apiResponse.isPro && apiResponse.hiddenCount > 0 && (
				<Alert
					variant="default"
					className="mb-6 bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
					<Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
					<AlertTitle className="font-semibold text-amber-700 dark:text-amber-300">
						{t('responses.viewAllFeedback')}
					</AlertTitle>
					<AlertDescription className="text-amber-600 dark:text-amber-400">
						{t('responses.receivedResponses', {
							total: apiResponse.totalLifetimeResponses,
							limit: apiResponse.viewableLimit,
							hidden: apiResponse.hiddenCount,
						})}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1 font-medium"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('responses.upgradeToProToSeeAll')}
						</Button>
					</AlertDescription>
				</Alert>
			)}

			{isLoading && !apiResponse ? (
				<ResponsesTableSkeleton />
			) : sortedResponses.length > 0 ||
			  (!apiResponse?.isPro &&
					apiResponse?.hiddenCount &&
					apiResponse.hiddenCount > 0 &&
					sortedResponses.length < itemsPerPage) ? (
				<>
					<ResponsesTable
						responses={sortedResponses}
						onResponseDeleted={refetch}
						onResponseSelect={(responseId) => {
							setSelectedResponseId(responseId)
							setSearchParams(
								(prev) => {
									const newParams = new URLSearchParams(prev)
									newParams.set('responseId', responseId)
									return newParams
								},
								{ replace: true }
							)
						}}
						isPro={apiResponse?.isPro}
						hiddenCount={apiResponse?.hiddenCount}
						itemsPerPage={itemsPerPage}
						sort={sort}
						onSortChange={handleSortChange}
					/>
				</>
			) : (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<div className="rounded-full bg-gray-100 p-3 dark:bg-gray-800 mb-4">
							<List className="h-6 w-6 text-muted-foreground" />
						</div>
						<h3 className="text-lg font-semibold mb-2">{t('responses.noResponsesFound')}</h3>
						<p className="text-muted-foreground max-w-md mb-8">
							{apiResponse?.totalMatchingResponses &&
							apiResponse.totalMatchingResponses > 0
								? t('responses.noResponsesMatchFilters')
								: t('responses.noSurveyResponsesYet')}
						</p>
						<Button variant="outline" onClick={resetFilters}>
							{t('responses.resetFilters')}
						</Button>
					</CardContent>
				</Card>
			)}

			{apiResponse && totalPages > 1 && !isLoading && (
				<div className="mt-6">
					<Pagination>
						<PaginationContent>
							<PaginationItem>
								<PaginationPrevious
									onClick={(e) => {
										e.preventDefault()
										if (currentPage > 1) {
											handlePageChange(currentPage - 1)
										}
									}}
									aria-disabled={currentPage === 1}
									className={cn(
										currentPage === 1 && 'pointer-events-none opacity-50'
									)}
									href="#"
								/>
							</PaginationItem>

							{/* Smart pagination logic based on total pages and current position */}
							{totalPages <= 5 ? (
								/* Show all pages for small page counts (1-5 pages) */
								Array.from({ length: totalPages }, (_, i) => i + 1).map(
									(pageNum) => (
										<PaginationItem key={pageNum}>
											<PaginationLink
												isActive={currentPage === pageNum}
												onClick={(e) => {
													e.preventDefault()
													if (currentPage !== pageNum) {
														handlePageChange(pageNum)
													}
												}}
												href="#">
												{pageNum}
											</PaginationLink>
										</PaginationItem>
									)
								)
							) : (
								/* Complex pagination for 6+ pages with ellipsis */
								<>
									{/* Always show page 1 */}
									<PaginationItem>
										<PaginationLink
											isActive={currentPage === 1}
											onClick={(e) => {
												e.preventDefault()
												if (currentPage !== 1) {
													handlePageChange(1)
												}
											}}
											href="#">
											1
										</PaginationLink>
									</PaginationItem>

									{/* Show ellipsis after page 1 if current page is far from start */}
									{currentPage > 3 && (
										<PaginationItem>
											<PaginationEllipsis />
										</PaginationItem>
									)}

									{/* Show previous page if current page > 2 and not near start */}
									{currentPage > 2 && currentPage <= 3 && (
										<PaginationItem>
											<PaginationLink
												onClick={(e) => {
													e.preventDefault()
													handlePageChange(2)
												}}
												href="#">
												2
											</PaginationLink>
										</PaginationItem>
									)}

									{/* Show current page if it's not 1 or the last page */}
									{currentPage > 1 && currentPage < totalPages && (
										<PaginationItem>
											<PaginationLink isActive>{currentPage}</PaginationLink>
										</PaginationItem>
									)}

									{/* Show next page if current page is near the end but not last */}
									{currentPage < totalPages - 1 &&
										currentPage >= totalPages - 2 && (
											<PaginationItem>
												<PaginationLink
													onClick={(e) => {
														e.preventDefault()
														handlePageChange(totalPages - 1)
													}}
													href="#">
													{totalPages - 1}
												</PaginationLink>
											</PaginationItem>
										)}

									{/* Show ellipsis before last page if current page is far from end */}
									{currentPage < totalPages - 2 && (
										<PaginationItem>
											<PaginationEllipsis />
										</PaginationItem>
									)}

									{/* Always show last page */}
									<PaginationItem>
										<PaginationLink
											isActive={currentPage === totalPages}
											onClick={(e) => {
												e.preventDefault()
												if (currentPage !== totalPages) {
													handlePageChange(totalPages)
												}
											}}
											href="#">
											{totalPages}
										</PaginationLink>
									</PaginationItem>
								</>
							)}

							<PaginationItem>
								<PaginationNext
									onClick={(e) => {
										e.preventDefault()
										if (currentPage < totalPages) {
											handlePageChange(currentPage + 1)
										}
									}}
									aria-disabled={currentPage === totalPages}
									className={cn(
										currentPage === totalPages &&
											'pointer-events-none opacity-50'
									)}
									href="#"
								/>
							</PaginationItem>
						</PaginationContent>
					</Pagination>
				</div>
			)}

			{selectedResponseId && (
				<ResponseDetailView
					responseId={selectedResponseId}
					onClose={() => {
						setSelectedResponseId(null)
						setSearchParams(
							(prev) => {
								const newParams = new URLSearchParams(prev)
								newParams.delete('responseId')
								return newParams
							},
							{ replace: true }
						)
					}}
				/>
			)}
		</div>
	)
}

export default Responses
